import { useState, useEffect, useRef, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useOfficerTitles } from "@/hooks/useOfficerTitles";
import axios from "axios";
import { toast } from "sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import UserManagement from "@/pages/UserManagement";
import AcceptInvite from "@/pages/AcceptInvite";
import Prospects from "@/pages/Prospects";
import ProspectChannelAnalytics from "@/pages/ProspectChannelAnalytics";
import Messages from "@/pages/Messages";
import UpdateLog from "@/pages/UpdateLog";
import MessageMonitor from "@/pages/MessageMonitor";
import EventCalendar from "@/pages/EventCalendar";
import CSVExportView from "@/pages/CSVExportView";
import ArchivedMembers from "@/pages/ArchivedMembers";
import ArchivedProspects from "@/pages/ArchivedProspects";
import DiscordAnalytics from "@/pages/DiscordAnalytics";
import VoiceHoursAnalytics from "@/pages/VoiceHoursAnalytics";
import WallOfHonor from "@/pages/WallOfHonor";
import Store from "@/pages/Store";
import SupporterStore from "@/pages/SupporterStore";
import QuarterlyReports from "@/pages/QuarterlyReports";
import AIKnowledgeManager from "@/pages/AIKnowledgeManager";
import OfficerTracking from "@/pages/OfficerTracking";
import FinancialAssistanceLedger from "@/pages/FinancialAssistanceLedger";
import QuizManager from "@/pages/QuizManager";
import QuizTaker from "@/pages/QuizTaker";
import SuggestionBox from "@/pages/SuggestionBox";
import PermissionPanel from "@/pages/PermissionPanel";
import DuesReminders from "@/pages/DuesReminders";
import Forms from "@/pages/Forms";
import PromotionPage from "@/pages/PromotionPage";
import DocumentTemplates from "@/pages/DocumentTemplates";
import TemplateEditor from "@/pages/TemplateEditor";
import SignDocument from "@/pages/SignDocument";
import Treasury from "@/pages/Treasury";
import NFCManager from "@/pages/NFCManager";
import DiscordBots from "@/pages/DiscordBots";
import DiscordLinking from "@/pages/DiscordLinking";
import UserMemberLinking from "@/pages/UserMemberLinking";
import Polls from "@/pages/Polls";
import DisconnectTool from "@/pages/DisconnectTool";
import Downloads from "@/pages/Downloads";
import RefactorHealth from "@/pages/RefactorHealth";
import QRCodeManager from "@/pages/QRCodeManager";
import LinkTree from "@/pages/LinkTree";
import LinkTreeManager from "@/pages/LinkTreeManager";
import EmailGroups from "@/pages/EmailGroups";
import TitleEditor from "@/pages/TitleEditor";
import ChapterEditor from "@/pages/ChapterEditor";
import ScriptlyNotes from "@/pages/ScriptlyNotes";
import MeetingTranscriptions from "@/pages/MeetingTranscriptions";
import MemberQuestionnaire from "@/pages/MemberQuestionnaire";
import HangaroundIntake from "@/pages/HangaroundIntake";
import DeparturesLog from "@/pages/DeparturesLog";
import ChatBot from "@/components/ChatBot";
import VersionWatcher from "@/components/VersionWatcher";
import { performCacheCleanup } from "@/components/ForceRefreshButton";
import GlobalRefreshFab from "@/components/GlobalRefreshFab";
import { Toaster } from "@/components/ui/sonner";

// Error Boundary to prevent white screen crashes
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("React Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-red-400 mb-4">Something went wrong</h2>
            <p className="text-slate-300 mb-4">The application encountered an error. Please try refreshing the page.</p>
            <button
              onClick={() => {
                localStorage.clear();
                window.location.href = '/login';
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg mr-2"
            >
              Clear & Login
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded-lg"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Component to save redirect URL before redirecting to login.
// Saves to sessionStorage synchronously during render (NOT in useEffect) so the
// value is persisted before <Navigate> unmounts the component.
function RedirectToLogin() {
  const location = useLocation();
  const fullPath = location.pathname + location.search;
  try {
    sessionStorage.setItem('redirectAfterLogin', fullPath);
  } catch (e) {
    // Private mode / quota — best-effort only
  }
  return <Navigate to="/login" replace />;
}


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Keep-alive component to prevent backend from sleeping
// Floating member tools (AI assistant + refresh fab) — hidden on the
// public Link Tree page so outside visitors' view stays clean even for
// logged-in members.
function MemberFabs() {
  const location = useLocation();
  if (location.pathname === "/linktree") return null;
  return (
    <>
      <ChatBot />
      <GlobalRefreshFab />
    </>
  );
}

function KeepAlive() {
  useEffect(() => {
    const ping = async () => {
      try {
        await axios.get(`${API}/ping`, { timeout: 5000 });
      } catch (error) {
        // Silently ignore ping errors - this is just keep-alive
        console.debug("Keep-alive ping failed:", error.message);
      }
    };

    // Initial ping
    ping();

    // Ping every 30 seconds
    const interval = setInterval(ping, 30000);

    return () => clearInterval(interval);
  }, []);

  return null;
}

// Component to handle message notifications
function MessageNotifier() {
  const previousUnreadCount = useRef(0);
  const isFirstLoad = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get(`${API}/messages/unread/count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const newCount = response.data.unread_count;

        // Check if we have new messages (count increased)
        // Don't show notification on first load
        if (!isFirstLoad.current && newCount > previousUnreadCount.current) {
          const newMessages = newCount - previousUnreadCount.current;
          toast.info(
            `You have ${newMessages} new private message${newMessages > 1 ? 's' : ''}!`,
            {
              duration: 5000,
              action: {
                label: 'View',
                onClick: () => navigate('/messages')
              }
            }
          );
        }

        // Update the counts
        previousUnreadCount.current = newCount;

        // Mark first load as complete
        if (isFirstLoad.current) {
          isFirstLoad.current = false;
        }
      } catch (error) {
        console.error("Failed to fetch unread messages count:", error);
      }
    };

    // Fetch immediately and then every 30 seconds
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [navigate]);

  return null;
}

function App() {
  const titleLists = useOfficerTitles();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [userChapter, setUserChapter] = useState(null);
  const [userTitle, setUserTitle] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('permissions');
    localStorage.removeItem('chapter');
    localStorage.removeItem('title');
    setIsAuthenticated(false);
    setUserRole(null);
    setUserPermissions(null);
    setUserChapter(null);
    setUserTitle(null);
  };

  // Function to refresh permissions from server
  const refreshPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { permissions, chapter, title, role } = response.data;
      
      // Update state and localStorage with fresh permissions
      setUserPermissions(permissions);
      setUserChapter(chapter);
      setUserTitle(title);
      setUserRole(role);
      
      localStorage.setItem('permissions', JSON.stringify(permissions));
      if (chapter) localStorage.setItem('chapter', chapter);
      if (title) localStorage.setItem('title', title);
      if (role) localStorage.setItem('role', role);
      
    } catch (error) {
      console.error("Failed to refresh permissions:", error);
      // If token is invalid, log out
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  // Refresh permissions periodically (every 2 minutes) to catch permission changes
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const intervalId = setInterval(() => {
      refreshPermissions();
    }, 2 * 60 * 1000); // 2 minutes
    
    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  // Refresh permissions on window focus (when user comes back to tab)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const handleFocus = () => {
      refreshPermissions();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const permissions = localStorage.getItem('permissions');
    const chapter = localStorage.getItem('chapter');
    const title = localStorage.getItem('title');

    // ── Magic-link auto-sign-in ─────────────────────────────────────────
    // Discord DMs each member a quiz URL with `?t=<signed-token>`. If we see
    // that param and the user is NOT already signed in, exchange it for a
    // session JWT, then strip the token from the URL bar so it doesn't leak
    // via referer/screenshot/bookmark.
    const url = new URL(window.location.href);
    const magicToken = url.searchParams.get('t');

    const stripMagicTokenFromUrl = () => {
      try {
        url.searchParams.delete('t');
        const clean = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
        window.history.replaceState({}, '', clean);
      } catch (e) { /* ignore */ }
    };

    if (magicToken) {
      if (token && role) {
        // Already authenticated — just strip the token, no exchange needed.
        stripMagicTokenFromUrl();
        setIsAuthenticated(true);
        setUserRole(role);
        try { setUserPermissions(permissions ? JSON.parse(permissions) : null); } catch { setUserPermissions(null); }
        setUserChapter(chapter || null);
        setUserTitle(title || null);
        refreshPermissions();
        setLoading(false);
        return;
      }
      // Not signed in → exchange the magic-link token for a real session.
      // After a successful exchange we deliberately purge browser cache +
      // service workers + sessionStorage (preserving the freshly-stored auth
      // keys) and reload. This gives the member an "incognito-style" fresh
      // quiz page every time — they see exactly what the server is serving
      // right now, with no stale CacheStorage / SW interception in the way.
      axios
        .post(`${API}/auth/magic-link/exchange`, { token: magicToken })
        .then(async (res) => {
          const d = res.data || {};
          if (d.token && d.role) {
            localStorage.setItem('token', d.token);
            localStorage.setItem('username', d.username || '');
            localStorage.setItem('role', d.role);
            localStorage.setItem('permissions', JSON.stringify(d.permissions || {}));
            if (d.chapter) localStorage.setItem('chapter', d.chapter);
            if (d.title) localStorage.setItem('title', d.title);

            // Purge caches NOW (auth keys are preserved by performCacheCleanup),
            // then strip ?t= and force a clean reload.
            await performCacheCleanup({ preserveAuth: true });
            stripMagicTokenFromUrl();
            const reloadUrl = new URL(window.location.href);
            reloadUrl.searchParams.set('_r', Date.now().toString());
            window.location.replace(reloadUrl.toString());
            return;
          }
        })
        .catch((err) => {
          const detail = err?.response?.data?.detail || 'Magic link expired or invalid. Please log in.';
          toast.error(detail);
          stripMagicTokenFromUrl();
          // Send them to /login so they don't sit on a broken quiz page with
          // just a toast that auto-dismisses.
          window.location.replace('/login');
        });
      return; // skip the plain localStorage path; promise above handles loading
    }
    // ── End magic-link path ─────────────────────────────────────────────

    if (token && role) {
      setIsAuthenticated(true);
      setUserRole(role);
      try {
        setUserPermissions(permissions ? JSON.parse(permissions) : null);
      } catch (e) {
        console.error("Failed to parse permissions:", e);
        setUserPermissions(null);
      }
      setUserChapter(chapter || null);
      setUserTitle(title || null);
      
      // Refresh permissions from server on initial load
      refreshPermissions();
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, username, role, permissions, chapter, title) => {
    localStorage.setItem('token', token);
    localStorage.setItem('username', username);
    localStorage.setItem('role', role);
    localStorage.setItem('permissions', JSON.stringify(permissions));
    if (chapter) {
      localStorage.setItem('chapter', chapter);
    }
    if (title) {
      localStorage.setItem('title', title);
    }
    setIsAuthenticated(true);
    setUserRole(role);
    setUserPermissions(permissions);
    setUserChapter(chapter);
    setUserTitle(title);
    
    // Check for redirect URL after login
    const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
    if (redirectUrl) {
      sessionStorage.removeItem('redirectAfterLogin');
      window.location.href = redirectUrl;
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/accept-invite"
            element={<AcceptInvite />}
          />
          <Route
            path="/supporter-store"
            element={<SupporterStore />}
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Dashboard onLogout={handleLogout} userRole={userRole} userPermissions={userPermissions} userChapter={userChapter} userTitle={userTitle} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/users"
            element={
              isAuthenticated && userPermissions?.manage_system_users ? (
                <UserManagement onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/prospects"
            element={
              isAuthenticated && (userPermissions?.view_prospects || (userRole === 'admin' && userChapter === 'National')) ? (
                <Prospects onLogout={handleLogout} userRole={userRole} userChapter={userChapter} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/prospect-channel-analytics"
            element={
              isAuthenticated && userRole === 'admin' && (userChapter === 'National' || userChapter === 'HA') ? (
                <ProspectChannelAnalytics />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/events"
            element={
              isAuthenticated ? (
                <EventCalendar onLogout={handleLogout} userRole={userRole} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/wall-of-honor"
            element={
              isAuthenticated ? (
                <WallOfHonor token={localStorage.getItem('token')} userRole={userRole} userChapter={userChapter} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/messages"
            element={
              isAuthenticated ? (
                <Messages onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/update-log"
            element={
              isAuthenticated && userRole === 'admin' ? (
                <UpdateLog />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/message-monitor"
            element={
              isAuthenticated && localStorage.getItem("username") === "Lonestar" ? (
                <MessageMonitor onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/export-view"
            element={
              isAuthenticated && (userRole === 'admin' || userPermissions?.view_export) ? (
                <CSVExportView />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/archived-members"
            element={
              isAuthenticated && (userRole === 'admin' || userPermissions?.admin_actions) ? (
                <ArchivedMembers />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/archived-prospects"
            element={
              isAuthenticated && (userRole === 'admin' || userPermissions?.admin_actions) ? (
                <ArchivedProspects />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/discord-analytics"
            element={
              isAuthenticated && (userPermissions?.view_discord_analytics || userRole === 'admin' || userPermissions?.admin_actions) ? (
                <DiscordAnalytics />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/voice-hours-analytics"
            element={
              isAuthenticated && (userPermissions?.view_discord_analytics || userRole === 'admin' || userPermissions?.admin_actions) ? (
                <VoiceHoursAnalytics />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/store"
            element={
              isAuthenticated ? (
                <Store userRole={userRole} userChapter={userChapter} userTitle={userTitle} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/quarterly-reports"
            element={
              isAuthenticated && userTitle && !['PM', 'Member', 'CC', 'CCLC', 'MD'].includes(userTitle) ? (
                <QuarterlyReports />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/ai-knowledge"
            element={
              isAuthenticated && (userRole === 'admin' || ['National President', 'National Vice President', 'National Secretary', 'NPrez', 'NVP', 'NSEC'].includes(userTitle)) ? (
                <AIKnowledgeManager token={localStorage.getItem('token')} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/officer-tracking"
            element={
              isAuthenticated ? (
                <OfficerTracking />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/financial-assistance"
            element={
              isAuthenticated ? (
                <FinancialAssistanceLedger />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/quizzes"
            element={
              isAuthenticated ? (
                <QuizManager />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/quiz/:quizId"
            element={
              isAuthenticated ? (
                <QuizTaker />
              ) : (
                <RedirectToLogin />
              )
            }
          />
          <Route
            path="/suggestions"
            element={
              isAuthenticated ? (
                <SuggestionBox />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/permissions"
            element={
              isAuthenticated ? (
                <PermissionPanel />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/dues-reminders"
            element={
              isAuthenticated ? (
                <DuesReminders />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/forms"
            element={
              isAuthenticated ? (
                <Forms />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/promotions"
            element={
              isAuthenticated ? (
                <PromotionPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/document-templates"
            element={
              isAuthenticated ? (
                <DocumentTemplates />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/document-templates/:templateId/edit"
            element={
              isAuthenticated ? (
                <TemplateEditor />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/sign/:signingToken"
            element={<SignDocument />}
          />
          <Route
            path="/questionnaire/:token"
            element={<MemberQuestionnaire />}
          />
          <Route
            path="/hangaround-intake"
            element={<HangaroundIntake />}
          />
          <Route
            path="/linktree"
            element={<LinkTree />}
          />
          <Route
            path="/linktree-admin"
            element={
              isAuthenticated && userPermissions?.manage_linktree ? (
                <LinkTreeManager />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/email-groups"
            element={
              isAuthenticated ? (
                <EmailGroups />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/scriptly"
            element={
              isAuthenticated && localStorage.getItem("chapter") === "National" ? (
                <ScriptlyNotes />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/title-editor"
            element={
              isAuthenticated && localStorage.getItem("chapter") === "National" && titleLists.national_admin_titles.includes(userTitle) ? (
                <TitleEditor />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/chapter-editor"
            element={
              isAuthenticated && localStorage.getItem("chapter") === "National" && userTitle === "CTO" ? (
                <ChapterEditor />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/treasury"
            element={
              isAuthenticated && (userPermissions?.view_treasury || userPermissions?.manage_treasury || userPermissions?.treasury_admin) ? (
                <Treasury />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/nfc"
            element={
              isAuthenticated && userPermissions?.manage_nfc ? (
                <NFCManager />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/discord-bots"
            element={
              isAuthenticated && userPermissions?.manage_discord_bots ? (
                <DiscordBots />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/discord-linking"
            element={
              isAuthenticated && userPermissions?.manage_discord_bots ? (
                <DiscordLinking />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/departures"
            element={
              isAuthenticated && userPermissions?.manage_discord_bots ? (
                <DeparturesLog />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/user-member-linking"
            element={
              isAuthenticated && userPermissions?.manage_system_users ? (
                <UserMemberLinking />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/polls"
            element={
              isAuthenticated && (userRole === 'admin' || userPermissions?.access_polls || userPermissions?.manage_polls) ? (
                <Polls />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/disconnect-tool"
            element={
              isAuthenticated ? (
                <DisconnectTool />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/downloads"
            element={
              isAuthenticated ? (
                <Downloads />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/refactor-health"
            element={
              isAuthenticated &&
              localStorage.getItem("chapter") === "National" &&
              titleLists.leadership_core_titles.includes(localStorage.getItem("title")) ? (
                <RefactorHealth />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/qr-codes"
            element={
              isAuthenticated && userPermissions?.manage_qr_codes ? (
                <QRCodeManager />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/transcriptions"
            element={
              isAuthenticated && (userRole === 'admin' || userPermissions?.view_transcriptions) ? (
                <MeetingTranscriptions />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
        <KeepAlive />
        <VersionWatcher />
        {isAuthenticated && <MessageNotifier />}
        {isAuthenticated && <MemberFabs />}
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;